import { useLayoutEffect, useRef, useState } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

/**
 * Incoming slide rises from below by exactly one stage height (px), in sync with scroll progress.
 * String vh/% keyframes often fail to interpolate in Framer, which leaves the next layer stuck off-screen.
 */
function StackLayer({ item, index, total, progress, stageHeight, dark }) {
  const n = total;
  const start = index / n;
  const end = (index + 1) / n;
  const coverStart = (index + 1) / n;
  const coverEnd = Math.min((index + 2) / n, 1);

  const H = Math.max(stageHeight, 1);

  const y = useTransform(progress, (p) => {
    if (index === 0) return 0;
    if (p <= start) return H;
    if (p >= end) return 0;
    const t = (p - start) / (end - start);
    return H * (1 - t);
  });

  const scale = useTransform(
    progress,
    index >= n - 1 ? [0, 1] : [0, coverStart, coverEnd, 1],
    index >= n - 1 ? [1, 1] : [1, 1, 0.9, 0.9]
  );

  const opacity = useTransform(
    progress,
    index >= n - 1 ? [0, 1] : [0, coverStart, coverEnd, 1],
    index >= n - 1 ? [1, 1] : [1, 1, 0.9, 0.82]
  );

  /* Use a stronger shadow on dark backgrounds to keep card separation visible. */
  const cardShadow = dark
    ? '0 28px 90px rgba(0,0,0,0.55), 0 12px 40px rgba(0,0,0,0.42), 0 2px 10px rgba(0,0,0,0.35)'
    : '0 28px 90px rgba(15,23,42,0.14), 0 12px 40px rgba(15,23,42,0.1), 0 2px 10px rgba(15,23,42,0.06)';

  return (
    <motion.div
      style={{
        position: 'absolute',
        inset: 0,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '84px',
        paddingInline: 'clamp(20px, 4vw, 40px)',
        zIndex: index,
        y,
        willChange: 'transform',
      }}
    >
      <motion.div
        style={{
          position: 'relative',
          width: 'min(86vw, 1080px)',
          flexShrink: 0,
          overflow: 'hidden',
          borderRadius: 'clamp(26px, 3vw, 40px)',
          background: dark ? '#111827' : '#ffffff',
          height: 'min(72vh, calc(min(86vw, 1080px) * 0.62))',
          scale,
          opacity,
          transformOrigin: 'center center',
          boxShadow: cardShadow,
          willChange: 'transform, opacity',
        }}
      >
        <img
          src={item.imageUrl}
          alt=""
          loading="eager"
          decoding="async"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: item.imageFit || 'cover',
            objectPosition: item.imagePosition || 'center center',
          }}
          draggable={false}
        />
        <div
          style={{
            pointerEvents: 'none',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.3) 45%, rgba(0,0,0,0.1) 100%)',
          }}
          aria-hidden
        />
        {item.tag ? (
          <div style={{
            position: 'absolute',
            left: 'clamp(14px, 2vw, 24px)',
            top: 'clamp(14px, 2vw, 24px)',
            borderRadius: 999,
            background: 'rgba(0,0,0,0.45)',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: '#fff',
          }}>
            {item.tag}
          </div>
        ) : null}
        {item.badge ? (
          <div style={{
            position: 'absolute',
            right: 'clamp(14px, 2vw, 24px)',
            top: 'clamp(14px, 2vw, 24px)',
            borderRadius: 999,
            background: 'rgba(255,255,255,0.92)',
            padding: '6px 14px',
            fontSize: '12px',
            fontWeight: 500,
            textTransform: 'uppercase',
            letterSpacing: '0.14em',
            color: '#111827',
          }}>
            {item.badge}
          </div>
        ) : null}
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: 'clamp(20px, 3vw, 44px)' }}>
          <h2
            style={{
              maxWidth: '22ch',
              fontSize: 'clamp(1.65rem, 4.5vw, 3.75rem)',
              fontWeight: 700,
              textTransform: 'uppercase',
              lineHeight: 0.92,
              letterSpacing: '0.01em',
              color: '#fff',
              textShadow: '0 2px 12px rgba(0,0,0,0.55)',
              fontFamily: "'Bebas Neue', system-ui, sans-serif",
            }}
          >
            {item.title}
          </h2>
          {item.description ? (
            <p style={{ marginTop: 12, maxWidth: '68ch', fontSize: 'clamp(0.92rem, 1.2vw, 1.08rem)', fontWeight: 300, lineHeight: 1.6, color: 'rgba(255,255,255,0.92)' }}>
              {item.description}
            </p>
          ) : null}
        </div>
      </motion.div>
    </motion.div>
  );
}

/**
 * Scroll-driven overlap stack: outer height n×100vh pins one sticky full-viewport stage;
 * all slides are absolute in that stage so they stay fixed and stack while you scroll.
 */
export function ScrollStackReveal({ items, dark = false, className = '' }) {
  const containerRef = useRef(null);
  const stageRef = useRef(null);
  const [stageHeight, setStageHeight] = useState(() =>
    typeof window !== 'undefined' ? window.innerHeight : 800
  );

  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;

    const ro = new ResizeObserver(() => {
      setStageHeight(el.clientHeight);
    });
    ro.observe(el);
    setStageHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });

  const n = items.length;

  if (n === 0) return null;

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ position: 'relative', width: '100%', background: dark ? '#0f1014' : '#ffffff', height: `${n * 100}vh` }}
    >
      <div
        ref={stageRef}
        style={{
          position: 'sticky',
          top: 0,
          isolation: 'isolate',
          height: '100vh',
          minHeight: '100dvh',
          width: '100%',
          overflow: 'hidden',
          background: dark ? '#0f1014' : '#fff',
        }}
      >
        {items.map((item, index) => (
          <StackLayer
            key={item.id}
            item={item}
            index={index}
            total={n}
            progress={scrollYProgress}
            stageHeight={stageHeight}
            dark={dark}
          />
        ))}
      </div>
    </div>
  );
}
